"use client";

import React, { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import LoadingScreen from "../shared/loading-screen";
import AuthenticatedUser from "./auth/authenticated-user";
import AuthForm from "./auth/auth-form";
import { withFrontendOrigin } from "@/lib/apiHeaders";
import {
  authFetch,
  clearAuthSession,
  fetchSessionUser,
  markAuthSessionActive,
  mightHaveAuthSession,
  parseAuthJson,
} from "@/lib/authClient";
import Image from "next/image";

interface User {
  id: string;
  email: string;
  name: string;
  role: "user" | "admin" | "staff";
  section?: string | null;
  newsletterSubscribed?: boolean;
}

type AuthMode = "login" | "register";

interface AuthSubmitPayload {
  mode: AuthMode;
  email: string;
  password: string;
  name?: string;
  surname?: string;
}

export default function LoginComponent() {
  const [user, setUser] = useState<User | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const router = useRouter();

  useEffect(() => {
    const checkSession = async () => {
      if (!mightHaveAuthSession()) {
        setAuthChecked(true);
        return;
      }
      try {
        const sessionUser = await fetchSessionUser({ clearStale: true });
        setUser(sessionUser as User | null);
      } catch {
        toast.error("Errore durante il controllo della sessione.");
      } finally {
        setAuthChecked(true);
      }
    };
    void checkSession();
  }, []);

  const handleAuthSubmit = async (
    payload: AuthSubmitPayload,
  ): Promise<boolean> => {
    const endpoint =
      payload.mode === "login" ? "/auth/login" : "/auth/register";

    try {
      const res = await authFetch(endpoint, {
        method: "POST",
        headers: withFrontendOrigin({ "Content-Type": "application/json" }),
        body: JSON.stringify({
          email: payload.email.trim().toLowerCase(),
          password: payload.password,
          ...(payload.mode === "register"
            ? {
                name: payload.name?.trim(),
                surname: payload.surname?.trim(),
              }
            : {}),
        }),
      });

      const body = await parseAuthJson<{ user?: User; error?: string; message?: string }>(res);

        if (!res.ok) {
        const msg: string =
          body?.error ||
          body?.message ||
          (res.status === 429
            ? "Troppi tentativi. Attendi qualche minuto e riprova."
            : `Errore ${res.status}`);

        toast.error(msg);
        return false;
      }

      if (payload.mode === "login") {
        let loggedInUser = (body?.user as User | undefined) ?? null;
        if (!loggedInUser) {
          // Fallback: il cookie HttpOnly può essere stato impostato anche se il body
          // JSON non è parsabile (es. compressione brotli lato CDN).
          loggedInUser = (await fetchSessionUser()) as User | null;
        }
        if (!loggedInUser) {
          toast.error(
            "Accesso non riuscito. Verifica le credenziali o riprova tra poco.",
          );
          return false;
        }
        markAuthSessionActive();
        setUser({ ...loggedInUser, section: loggedInUser.section ?? null });
        window.dispatchEvent(new Event("auth:changed"));
        toast.success("Login effettuato.");
        return true;
      }

      if (payload.mode === "register") {
        toast.success("Controlla la tua email", {
          description:
            "Ti abbiamo inviato le istruzioni per l'account: conferma, accesso o recupero password. Se non trovi il messaggio, guarda anche nello spam.",
          duration: 10_000,
        });
        return true;
      }

      toast.error("Risposta inattesa dal server. Riprova.");
      return false;
    } catch {
      toast.error("Errore durante l'autenticazione. Riprova.");
      return false;
    }
  };

  const handleLogout = async () => {
    try {
      await authFetch("/auth/logout", { method: "POST" });
    } catch {
      // ignore
    } finally {
      clearAuthSession(true);
      setUser(null);
      toast.success("Logout effettuato.");
      router.push("/");
    }
  };

  const handleDeleteAccount = async () => {
    const sessionUser = await fetchSessionUser();
    if (!sessionUser) {
      toast.error("Non sei autenticato.");
      return;
    }

    try {
      const res = await authFetch("/auth/delete-user", { method: "DELETE" });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(body?.error || "Errore durante l'eliminazione.");
        return;
      }

      clearAuthSession(true);
      setUser(null);
      toast.success("Account eliminato.");
      router.push("/");
    } catch {
      toast.error("Errore di rete durante l'eliminazione.");
    }
  };

  return (
    <>
      <LoadingScreen show={!authChecked} fullscreen />
      {authChecked && (
        <div className="h-[90vh] sm:h-[80vh] lg:h-[90vh] lg:mt-0 flex items-center">
          {user ? (
            <AuthenticatedUser
              user={user}
              handleLogout={handleLogout}
              handleDeleteAccount={handleDeleteAccount}
              onUserChange={setUser}
            />
          ) : (
            <AuthForm isLogin={true} onSubmit={handleAuthSubmit} />
          )}

          <div className="hidden md:flex w-1/2 h-screen items-center justify-center">
            <Image
              src="/svg/svgbambini2.svg"
              alt="illustrazione scuola materna"
              className="w-full h-auto max-w-4xl mx-auto rounded-xl object-contain"
              width={500}
              height={500}
              loading="lazy"
            />
          </div>
        </div>
      )}
    </>
  );
}
